/**
 * 导航动作：组件不直接操作 Router 的 push/replace 语义，统一经这里。
 *
 * - Tab 切换不堆历史：栈顶是 home 时 replace（同 Tab no-op）；栈顶是深层页
 *   （桌面双栏开着会话点 Tab）时 push 一层 home——移动端此时返回键回到会话，
 *   桌面端右栏会话仍在栈里继续显示（见 shell/projection.ts 的投影规则）。
 * - openChat 对「已在该会话」no-op，防重复点击堆栈。
 */
import { beginInteraction, INTERACTIONS } from '~/perf/interaction';
import { activePanelRoute } from './projection';
import type { Router } from './router';
import { HOME_CHATS, type HomeTab } from './routes';

export function switchHomeTab(router: Router, tab: HomeTab): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'home') {
    if (top.tab !== tab) router.replace({ kind: 'home', tab });
  } else {
    router.push({ kind: 'home', tab });
  }
}

export function openChat(router: Router, conversationKey: string): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'chat' && top.conversationKey === conversationKey) return;
  // 交互反馈打点（20c）：这里是「进会话」的唯一漏斗（列表行/通讯录/搜索/通知点击
  // 都走它）。栈顶已是另一个会话 = 桌面双栏的直接切换，与从列表进入分两个指标；
  // chat-rows 是同一起点的后续里程碑（消息行到位，含读库）。no-op 不开 span。
  beginInteraction(
    [top?.kind === 'chat' ? INTERACTIONS.switchChat : INTERACTIONS.openChat, INTERACTIONS.chatRows],
    conversationKey,
  );
  router.push({ kind: 'chat', conversationKey });
}

/**
 * 通知点击落位（21aj）：进会话**并保证栈底有首页**。
 *
 * 普通的 `openChat` 是往当前栈上 push。正常路径下栈底一定是首页——冷启动走
 * `stackForPath('/chat/...')` 会垫成 `[首页, 会话页]`，应用已开着时栈底本来就是
 * 首页。但通知这条路有两个入口（SW 的 `openWindow` 冷启 / focus 已开窗口后
 * postMessage），只有前者经过垫栈；万一落到一个只有会话页的退化栈上，用户从
 * 通知进来一返回就是空的（人工报「从 web push 点进会话，返回是空白的」）。
 *
 * 只处理**退化栈**（只有一层且不是首页），其余情形原样 push——
 * 用户正停在群设置页时点推送，返回仍该回群设置，不该被改成回首页。
 */
export function openChatFromNotification(router: Router, conversationKey: string): void {
  const stack = router.getNav().stack;
  if (stack.length === 1 && stack[0]?.kind !== 'home') router.replace(HOME_CHATS);
  openChat(router, conversationKey);
}

/** 我创建的表情包（14.17b；贴纸面板「管理」钮入口） */
export function openStickerPacks(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'sticker-packs') return;
  router.push({ kind: 'sticker-packs' });
}

/** 创建（packId=0）/修改（packId>0）表情包页 */
export function openStickerPackEdit(router: Router, packId: number): void {
  router.push({ kind: 'sticker-pack-edit', packId });
}

/** 搜索加人页（14.19b；通讯录页顶部搜索条入口） */
export function openContactSearch(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'contact-search') return;
  router.push({ kind: 'contact-search' });
}

/** 申请处理页（14.20；通讯录四宫格「新的朋友/群通知」入口） */
export function openContactApplies(router: Router, applyKind: 'friend' | 'group'): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'contact-applies' && top.applyKind === applyKind) return;
  router.push({ kind: 'contact-applies', applyKind });
}

/** 分组管理页（14.22b；通讯录分组 Tab 头部入口） */
export function openFriendGroups(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'friend-groups') return;
  router.push({ kind: 'friend-groups' });
}

/** 黑名单页（14.22b-2；通讯录四宫格入口） */
export function openBlacklist(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'blacklist') return;
  router.push({ kind: 'blacklist' });
}

/** 系统公告页（14.24；通讯录四宫格入口） */
export function openAnnouncements(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'announcements') return;
  router.push({ kind: 'announcements' });
}

/** 建群页（14.23b；通讯录页顶部动作区入口） */
export function openCreateGroup(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'create-group') return;
  router.push({ kind: 'create-group' });
}

/**
 * 建群成功落位（14.23b）：用新群聊天页**替换**栈顶的建群页——返回键回通讯录
 * 而不是回到已提交完成的表单（旧版 pushReplacement 同语义）。
 */
export function openChatAfterGroupCreated(router: Router, conversationKey: string): void {
  router.replace({ kind: 'chat', conversationKey });
}

/**
 * 会话资料入口（21q）：私聊→好友详情、群聊→群设置。
 * 桌面上这就是「开资料面板」；已经开着（栈顶是该会话的资料面板那一串）就**收起**，
 * 即栈里把这一串弹掉——所以标题旁的按钮是 ⓘ / × 一个键两态，与人工给的图一致。
 * 移动端没有并存形态，按钮不渲染，标题点击照旧是「进详情页」。
 */
export function toggleConversationInfo(router: Router, conversationKey: string): void {
  if (activePanelRoute(router.getNav()) !== undefined) {
    // 面板（含面板里再点进去的子页）整串弹掉，回到纯聊天
    router.popWhile((r) => r.kind !== 'chat');
    return;
  }
  if (conversationKey.startsWith('dm:')) {
    openFriendDetail(router, Number(conversationKey.slice('dm:'.length)));
  } else {
    openGroupSettings(router, Number(conversationKey.slice('group:'.length)));
  }
}

/** 群设置主页（14.25b；群聊标题区入口） */
export function openGroupSettings(router: Router, groupId: number): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'group-settings' && top.groupId === groupId) return;
  router.push({ kind: 'group-settings', groupId });
}

/** 群管理页（14.26b；群设置页「群管理」行入口，owner/admin） */
export function openGroupManage(router: Router, groupId: number): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'group-manage' && top.groupId === groupId) return;
  router.push({ kind: 'group-manage', groupId });
}

/** 群成员列表页（14.27b；群设置页「群成员」行入口） */
export function openGroupMembers(router: Router, groupId: number): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'group-members' && top.groupId === groupId) return;
  router.push({ kind: 'group-members', groupId });
}

/** 邀请好友入群页（14.27c；群设置页「邀请好友」行入口） */
export function openGroupInvite(router: Router, groupId: number): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'group-invite' && top.groupId === groupId) return;
  router.push({ kind: 'group-invite', groupId });
}

/** 成员进出记录页（14.27c；群设置页仅群主行入口） */
export function openGroupLog(router: Router, groupId: number): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'group-log' && top.groupId === groupId) return;
  router.push({ kind: 'group-log', groupId });
}

/** 群公告页（14.28；群设置页「群公告」行入口，全员可进） */
export function openGroupNotice(router: Router, groupId: number): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'group-notice' && top.groupId === groupId) return;
  router.push({ kind: 'group-notice', groupId });
}

/** 群二维码页（14.32；群设置页「群二维码」行入口，全员可进） */
export function openGroupQrCode(router: Router, groupId: number): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'group-qrcode' && top.groupId === groupId) return;
  router.push({ kind: 'group-qrcode', groupId });
}

/**
 * 退群/解散成功后的退场（14.26b）：把栈顶的该群管理页/成员页/邀请页/进出记录页/
 * 设置页与其下方该群聊天页一次弹干净（exitAfterFriendPurged 同口径——会话已本地
 * 购销，不能留在历史）。
 */
export function exitAfterGroupLeft(router: Router, groupId: number): void {
  const groupKey = `group:${groupId}`;
  router.popWhile(
    (r) =>
      ((r.kind === 'group-manage' ||
        r.kind === 'group-members' ||
        r.kind === 'group-invite' ||
        r.kind === 'group-log' ||
        r.kind === 'group-notice' ||
        r.kind === 'group-qrcode' ||
        r.kind === 'group-settings') &&
        r.groupId === groupId) ||
      (r.kind === 'chat' && r.conversationKey === groupKey),
  );
}

/** 编辑资料页（14.29b；我的 Tab 资料卡入口） */
export function openMyProfile(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-profile') return;
  router.push({ kind: 'me-profile' });
}

/** 隐私设置页（14.30；我的 Tab「隐私设置」行入口） */
export function openMyPrivacy(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-privacy') return;
  router.push({ kind: 'me-privacy' });
}

/** 路线设置页（14.39b；我的 Tab「路线设置」行入口；登录页的开关是内联行不走路由） */
export function openMyRoute(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-route') return;
  router.push({ kind: 'me-route' });
}

/** 外观与通知设置页（14.31c；我的 Tab「外观与通知」行入口） */
export function openMyAppearance(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-appearance') return;
  router.push({ kind: 'me-appearance' });
}

/** 我的二维码页（14.32；我的 Tab「我的二维码」行入口） */
export function openMyQrCode(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-qrcode') return;
  router.push({ kind: 'me-qrcode' });
}

/** 账号管理页（14.34b；我的 Tab「多账号与设备」行入口） */
export function openMyAccounts(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-accounts') return;
  router.push({ kind: 'me-accounts' });
}

/** 添加账号页（14.34b-2；账号管理页「添加账号」行入口） */
export function openAddAccount(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'add-account') return;
  router.push({ kind: 'add-account' });
}

/** 登录设备列表页（14.34b-3；入口=账号管理页「登录设备」行） */
export function openLoginDevices(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-devices') return;
  router.push({ kind: 'me-devices' });
}

/** 关于页（14.35a；入口=我的 Tab「关于与反馈」行） */
export function openAbout(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-about') return;
  router.push({ kind: 'me-about' });
}

/** 意见反馈表单页（14.35b；入口=关于页「意见反馈」行） */
export function openFeedbackForm(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-feedback' && top.target === undefined) return;
  router.push({ kind: 'me-feedback' });
}

/**
 * 举报预填（14.35d；入口=群设置页「举报该群」/ 好友详情页「举报用户」）：
 * 带路径段进表单页，形态+目标 ID 预填只读。id ≤ 0（理论上不可能——两处入口的
 * ID 都来自路由参数且解析时已要求正整数）退回手选形态，旧版「取不到 ID 手输」
 * 同口径不让用户卡死。
 */
export function openFeedbackReport(
  router: Router,
  scope: 'group' | 'private',
  id: number,
): void {
  if (id <= 0) {
    openFeedbackForm(router);
    return;
  }
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-feedback' && top.target?.scope === scope && top.target.id === id) return;
  router.push({ kind: 'me-feedback', target: { scope, id } });
}

/** 反馈历史页（14.35c；入口=关于页「我的反馈」行） */
export function openFeedbackHistory(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-feedback-history') return;
  router.push({ kind: 'me-feedback-history' });
}

/** 归档会话页（14.36b；入口=会话列表顶部「归档会话」入口行） */
export function openArchive(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'archive') return;
  router.push({ kind: 'archive' });
}

/** 安装引导页（16b；入口=关于页「安装到主屏幕」行，standalone 下不显示该行） */
export function openInstallGuide(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'install') return;
  router.push({ kind: 'install' });
}

/** 两步验证管理页（14.34b-3；入口=账号管理页「两步验证」行） */
export function openTwoFa(router: Router): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'me-2fa') return;
  router.push({ kind: 'me-2fa' });
}

/** 好友详情页（14.21b；通讯录好友行 / 私聊标题区入口） */
export function openFriendDetail(router: Router, friendId: number): void {
  const stack = router.getNav().stack;
  const top = stack[stack.length - 1];
  if (top?.kind === 'friend-detail' && top.friendId === friendId) return;
  router.push({ kind: 'friend-detail', friendId });
}

/**
 * 从好友详情发起聊天（14.21b）：下一层恰是该好友的聊天页（从聊天页标题进的
 * 详情）时直接出栈回去，避免历史里堆两个同会话条目；否则 push 新聊天页。
 */
export function openChatFromFriendDetail(router: Router, conversationKey: string): void {
  const stack = router.getNav().stack;
  const below = stack[stack.length - 2];
  if (below?.kind === 'chat' && below.conversationKey === conversationKey) {
    router.back();
    return;
  }
  openChat(router, conversationKey);
}

/**
 * 删好友/拉黑成功后的退场（14.21b）：把栈顶的该好友详情页与其下方同好友的
 * 聊天页（从聊天页进详情的入口路径）一次弹干净——会话已被购销，聊天页不能
 * 留在历史里。落点是通讯录 Tab 或更下层页面（popWhile 保栈底）。
 */
export function exitAfterFriendPurged(router: Router, friendId: number): void {
  const dmKey = `dm:${friendId}`;
  router.popWhile(
    (r) =>
      (r.kind === 'friend-detail' && r.friendId === friendId) ||
      (r.kind === 'chat' && r.conversationKey === dmKey),
  );
}
